import { format, getDay, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { MakeupBooking } from '~entities/makeup/api/book-makeup';
import { useGetMakeupBalances } from '~entities/makeup/api/get-makeup-balances';
import { useGetMakeupOccurrenceCapacity } from '~entities/makeup/api/get-makeup-occurrence-capacity';
import { useGetMakeupsByStudent } from '~entities/makeup/api/get-makeups-by-student';
import { useGetMakeupTargets } from '~entities/makeup/api/get-makeup-targets';
import { useRescheduleMakeupMutation } from '~entities/makeup/api/reschedule-makeup';
import { useUpdateMakeupStatusMutation } from '~entities/makeup/api/update-makeup-status';
import type { Student } from '~entities/student';
import { parseTimeToMinutes as parseSharedTimeToMinutes } from '~features/room-overview/lib/timeline';

import type { Days } from '~shared/constants/days';
import { parseToNoonDate } from '~shared/helpers/datetime';
import { Calendar } from '~shared/shadcn/calendar';
import { cn } from '~shared/shadcn/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';
import { Textarea } from '~shared/shadcn/ui/textarea';
import {
  getErrorMessage,
  isMakeupCapacityFullError,
} from '~shared/utils/error-handler';

const DAYS_OF_WEEK = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
} as const;

type DayNumbers = (typeof DAYS_OF_WEEK)[keyof typeof DAYS_OF_WEEK];

const ACTIVE_MAKEUP_STATUSES = new Set(['requested', 'scheduled']);

function parseTimeToMinutes(time?: string): number | null {
  if (!time?.trim()) return null;
  return parseSharedTimeToMinutes(time.trim());
}

function timesOverlap(
  startA?: string,
  endA?: string,
  startB?: string,
  endB?: string,
): boolean {
  const aStart = parseTimeToMinutes(startA);
  const aEnd = parseTimeToMinutes(endA);
  const bStart = parseTimeToMinutes(startB);
  const bEnd = parseTimeToMinutes(endB);
  if (aStart == null || aEnd == null || bStart == null || bEnd == null) {
    return false;
  }
  return aStart < bEnd && bStart < aEnd;
}

function getEnrollmentId(
  value:
    | MakeupBooking['targetEnrollmentId']
    | MakeupBooking['sourceEnrollmentId'],
): string {
  if (!value) return '';
  return typeof value === 'string' ? value : value._id;
}

function getEnrollmentTitle(
  value:
    | MakeupBooking['targetEnrollmentId']
    | MakeupBooking['sourceEnrollmentId'],
): string {
  if (!value) return 'Class';
  if (typeof value === 'string') return 'Class';
  return value.title || 'Class';
}

function getEnrollmentTimes(
  value: MakeupBooking['targetEnrollmentId'],
): { startTime?: string; endTime?: string } {
  if (!value || typeof value === 'string') return {};
  return { startTime: value.startTime, endTime: value.endTime };
}

function getStudentName(
  student: Student | null,
  booking: MakeupBooking | null,
): string {
  if (student?.name) return student.name;
  const raw = booking?.studentId;
  if (!raw) return 'student';
  if (typeof raw === 'string') return 'student';
  return (
    raw.name ||
    [raw.firstName, raw.lastName].filter(Boolean).join(' ') ||
    'student'
  );
}

function formatDisplayDate(value: string | undefined): string {
  if (!value) return '—';
  try {
    return format(parseISO(String(value).slice(0, 10)), 'MMM d, yyyy');
  } catch {
    return String(value).slice(0, 10);
  }
}

function createMakeupDisabledDates({
  dayNames,
  startDate,
  endDate,
  isDateConflicted,
}: {
  dayNames: Array<Days>;
  startDate: string | Date | null | undefined;
  endDate: string | Date | null | undefined;
  isDateConflicted?: (day: Date) => boolean;
}): (day: Date) => boolean {
  const allowedDays = new Set(dayNames.map((day) => DAYS_OF_WEEK[day]));
  const parsedStart = parseToNoonDate(startDate);
  const parsedEnd = parseToNoonDate(endDate);

  if (!parsedStart || !parsedEnd) {
    return () => true;
  }

  const start = startOfDay(parsedStart);
  const end = startOfDay(parsedEnd);

  return (day: Date): boolean => {
    const normalizedDay = startOfDay(day);
    return (
      !allowedDays.has(getDay(day) as DayNumbers) ||
      isBefore(normalizedDay, start) ||
      isAfter(normalizedDay, end) ||
      (isDateConflicted?.(normalizedDay) ?? false)
    );
  };
}

interface ManageMakeupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: MakeupBooking | null;
  student: Student | null;
  sessionId: string;
  sendConfirmationDefault?: boolean;
}

export function ManageMakeupSheet({
  open,
  onOpenChange,
  booking,
  student,
  sessionId,
  sendConfirmationDefault = true,
}: ManageMakeupSheetProps) {
  const [mode, setMode] = useState<'details' | 'reschedule'>('details');
  const [targetEnrollmentId, setTargetEnrollmentId] = useState('');
  const [targetDate, setTargetDate] = useState<Date | undefined>();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [notes, setNotes] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState(
    sendConfirmationDefault,
  );
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [capacityOverrideOpen, setCapacityOverrideOpen] = useState(false);
  const [capacityOverrideMessage, setCapacityOverrideMessage] = useState('');
  const [cachedBooking, setCachedBooking] = useState<MakeupBooking | null>(
    booking,
  );
  const [cachedStudent, setCachedStudent] = useState<Student | null>(student);

  // Keep caches fresh when query objects update, without resetting the form.
  useEffect(() => {
    if (!open) return;
    if (booking) setCachedBooking(booking);
    if (student) setCachedStudent(student);
  }, [open, booking, student]);

  // Hydrate / reset only when the sheet opens or a different booking is opened.
  // Live query refetches must not kick staff out of an in-progress reschedule.
  useEffect(() => {
    if (!open) return;
    setMode('details');
    setCancelConfirmOpen(false);
    setCapacityOverrideOpen(false);
    setCapacityOverrideMessage('');
    setShowDatePicker(false);

    if (booking) {
      setTargetEnrollmentId(getEnrollmentId(booking.targetEnrollmentId));
      const dateKey = String(booking.targetDate).slice(0, 10);
      const parsed = parseToNoonDate(dateKey);
      setTargetDate(parsed ?? undefined);
      setNotes(booking.notes ?? '');
    } else {
      setTargetEnrollmentId('');
      setTargetDate(undefined);
      setNotes('');
    }
  }, [open, booking?._id]);

  useEffect(() => {
    if (!open) return;
    setSendConfirmation(sendConfirmationDefault);
  }, [open, sendConfirmationDefault]);

  const activeBooking = booking ?? cachedBooking;
  const activeStudent = student ?? cachedStudent;
  const studentId =
    activeStudent?._id ||
    (typeof activeBooking?.studentId === 'string'
      ? activeBooking.studentId
      : activeBooking?.studentId._id) ||
    '';
  const sourceEnrollmentId = activeBooking
    ? getEnrollmentId(activeBooking.sourceEnrollmentId)
    : '';
  const canManage =
    activeBooking?.status === 'scheduled' ||
    activeBooking?.status === 'requested';

  const {
    data: eligibleClasses = [],
    isLoading: isLoadingClasses,
    isError: isClassesError,
  } = useGetMakeupTargets(
    {
      studentId,
      sessionId,
      sourceEnrollmentId,
    },
    open &&
      mode === 'reschedule' &&
      !!studentId &&
      !!sessionId &&
      !!sourceEnrollmentId,
  );

  const {
    data: balances,
    isLoading: isLoadingBalances,
    isError: isBalancesError,
  } = useGetMakeupBalances(
    { studentId, sessionId },
    open && !!studentId && !!sessionId,
  );

  const { data: studentMakeups = [] } = useGetMakeupsByStudent(
    { studentId, sessionId },
    open && mode === 'reschedule' && !!studentId && !!sessionId,
  );

  const rescheduleMutation = useRescheduleMakeupMutation();
  const cancelMutation = useUpdateMakeupStatusMutation();

  const activeMakeups = useMemo(
    () =>
      studentMakeups.filter(
        (item) =>
          ACTIVE_MAKEUP_STATUSES.has(item.status) &&
          item._id !== activeBooking?._id,
      ),
    [studentMakeups, activeBooking?._id],
  );

  const selectedClass = useMemo(
    () => eligibleClasses.find((item) => item._id === targetEnrollmentId),
    [eligibleClasses, targetEnrollmentId],
  );

  const targetDateKey = targetDate ? format(targetDate, 'yyyy-MM-dd') : '';
  const {
    data: occurrenceCapacity,
    isLoading: isLoadingCapacity,
    isError: isCapacityError,
  } = useGetMakeupOccurrenceCapacity(
    {
      enrollmentId: targetEnrollmentId,
      date: targetDateKey,
    },
    open &&
      mode === 'reschedule' &&
      !!targetEnrollmentId &&
      !!targetDateKey,
  );

  const disabledDates = useMemo(() => {
    if (!selectedClass) return () => true;

    const isDateConflicted = (day: Date) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      return activeMakeups.some((item) => {
        const bookingDate = String(item.targetDate).slice(0, 10);
        if (bookingDate !== dateKey) return false;
        if (getEnrollmentId(item.targetEnrollmentId) === selectedClass._id) {
          return true;
        }
        const existingTimes = getEnrollmentTimes(item.targetEnrollmentId);
        return timesOverlap(
          selectedClass.startTime,
          selectedClass.endTime,
          existingTimes.startTime,
          existingTimes.endTime,
        );
      });
    };

    return createMakeupDisabledDates({
      dayNames: selectedClass.days as Days[],
      startDate: selectedClass.startDate,
      endDate: selectedClass.endDate,
      isDateConflicted,
    });
  }, [selectedClass, activeMakeups]);

  const familyRemaining = balances?.family.remaining ?? 0;
  const studentRemaining = balances?.student.remaining ?? 0;
  const studentDisplayName = getStudentName(activeStudent, activeBooking);

  const canSubmitReschedule =
    open &&
    mode === 'reschedule' &&
    canManage &&
    !!activeBooking &&
    !!targetEnrollmentId &&
    !!targetDate;

  const handleCancel = async () => {
    if (!activeBooking) return;
    try {
      await cancelMutation.mutateAsync({
        makeupId: activeBooking._id,
        status: 'cancelled',
      });
      toast.success('Makeup cancelled.');
      setCancelConfirmOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to cancel makeup.'));
    }
  };

  const handleReschedule = async (allowOverCapacity = false) => {
    if (!activeBooking || !targetEnrollmentId || !targetDate) {
      toast.error('Select a target class and date.');
      return;
    }

    try {
      await rescheduleMutation.mutateAsync({
        makeupId: activeBooking._id,
        targetEnrollmentId,
        targetDate: format(targetDate, 'yyyy-MM-dd'),
        notes: notes.trim(),
        sendConfirmation,
        allowOverCapacity: allowOverCapacity || undefined,
      });
      setCapacityOverrideOpen(false);
      toast.success(
        allowOverCapacity
          ? 'Makeup rescheduled over class capacity.'
          : 'Makeup rescheduled.',
      );
      onOpenChange(false);
    } catch (error) {
      if (!allowOverCapacity && isMakeupCapacityFullError(error)) {
        setCapacityOverrideMessage(
          getErrorMessage(
            error,
            'This class is at capacity for the selected date.',
          ),
        );
        setCapacityOverrideOpen(true);
        return;
      }
      toast.error(getErrorMessage(error, 'Failed to reschedule makeup.'));
    }
  };

  const classSelectPlaceholder = (() => {
    if (isLoadingClasses) return 'Loading eligible classes…';
    if (isClassesError) return 'Could not load classes';
    if (eligibleClasses.length === 0) return 'No eligible classes';
    return 'Select class';
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full overflow-y-auto sm:max-w-lg'>
        <SheetHeader>
          <SheetTitle>
            {mode === 'reschedule' ? 'Reschedule makeup' : 'Makeup booking'}
          </SheetTitle>
          <SheetDescription>
            {mode === 'reschedule'
              ? `Move ${studentDisplayName}'s makeup to a new class or date.`
              : `Details for ${studentDisplayName}'s makeup booking.`}
          </SheetDescription>
        </SheetHeader>

        <div className='mt-6 space-y-5'>
          <div className='rounded-lg border p-4'>
            <p className='mb-2 text-sm font-medium'>Balances</p>
            {isLoadingBalances ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Loader2 className='size-4 animate-spin' />
                Loading balances…
              </div>
            ) : isBalancesError || !balances ? (
              <p className='text-sm text-destructive'>
                Could not load makeup balances.
              </p>
            ) : (
              <div className='grid grid-cols-2 gap-3 text-sm'>
                <div>
                  <p className='text-muted-foreground'>Family credits</p>
                  <p className='font-medium'>
                    {familyRemaining} remaining
                    <span className='text-muted-foreground'>
                      {' '}
                      / {balances.family.limit ?? 0}
                    </span>
                  </p>
                </div>
                <div>
                  <p className='text-muted-foreground'>Student allotment</p>
                  <p className='font-medium'>
                    {studentRemaining} remaining
                    <span className='text-muted-foreground'>
                      {' '}
                      / {balances.student.limit ?? 0}
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>

          {mode === 'details' && activeBooking ? (
            <div className='space-y-3 rounded-lg border p-4 text-sm'>
              <div>
                <p className='text-muted-foreground'>Absence</p>
                <p className='font-medium'>
                  {getEnrollmentTitle(activeBooking.sourceEnrollmentId)} ·{' '}
                  {formatDisplayDate(activeBooking.sourceAttendanceDate)}
                </p>
              </div>
              <div>
                <p className='text-muted-foreground'>Makeup class</p>
                <p className='font-medium'>
                  {getEnrollmentTitle(activeBooking.targetEnrollmentId)}
                </p>
              </div>
              <div>
                <p className='text-muted-foreground'>Makeup date</p>
                <p className='font-medium'>
                  {formatDisplayDate(activeBooking.targetDate)}
                </p>
              </div>
              <div>
                <p className='text-muted-foreground'>Status</p>
                <p className='font-medium capitalize'>
                  {String(activeBooking.status).replace('_', ' ')}
                </p>
              </div>
              {activeBooking.notes ? (
                <div>
                  <p className='text-muted-foreground'>Notes</p>
                  <p className='font-medium whitespace-pre-wrap'>
                    {activeBooking.notes}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {mode === 'reschedule' ? (
            <>
              <div className='space-y-2'>
                <label className='text-sm font-medium'>Target class</label>
                <Select
                  value={targetEnrollmentId}
                  onValueChange={(value) => {
                    setTargetEnrollmentId(value);
                    setTargetDate(undefined);
                  }}
                  disabled={
                    isLoadingClasses ||
                    isClassesError ||
                    eligibleClasses.length === 0
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={classSelectPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {eligibleClasses.map((classItem) => (
                      <SelectItem key={classItem._id} value={classItem._id}>
                        {classItem.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <label className='text-sm font-medium'>Target date</label>
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <Button
                      variant='outline'
                      disabled={!selectedClass}
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !targetDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className='mr-2 size-4' />
                      {targetDate
                        ? format(targetDate, 'PPP')
                        : 'Pick a class date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-auto p-0' align='start'>
                    <Calendar
                      mode='single'
                      selected={targetDate}
                      onSelect={(day) => {
                        setTargetDate(day);
                        setShowDatePicker(false);
                      }}
                      disabled={disabledDates}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {targetDateKey ? (
                  <p
                    className={cn(
                      'text-sm',
                      occurrenceCapacity?.isFull
                        ? 'text-destructive'
                        : 'text-muted-foreground',
                    )}
                  >
                    {isLoadingCapacity
                      ? 'Checking seats for this date…'
                      : isCapacityError
                        ? 'Could not load seats for this date.'
                        : occurrenceCapacity?.remainingSeats == null
                          ? 'No seat limit set for this class.'
                          : occurrenceCapacity.isFull
                            ? `Full on this date (${occurrenceCapacity.occupancy}/${occurrenceCapacity.maxSize}). Staff can still override.`
                            : `${occurrenceCapacity.remainingSeats} seat${
                                occurrenceCapacity.remainingSeats === 1
                                  ? ''
                                  : 's'
                              } left on this date (${occurrenceCapacity.occupancy}/${occurrenceCapacity.maxSize}).`}
                  </p>
                ) : null}
              </div>

              <div className='space-y-2'>
                <label htmlFor='reschedule-notes' className='text-sm font-medium'>
                  Notes
                </label>
                <Textarea
                  id='reschedule-notes'
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder='Optional note for staff'
                  rows={3}
                />
              </div>

              <label className='flex items-center gap-2 text-sm'>
                <Checkbox
                  checked={sendConfirmation}
                  onCheckedChange={(checked) =>
                    setSendConfirmation(checked === true)
                  }
                />
                Send confirmation email to family
              </label>
            </>
          ) : null}
        </div>

        <SheetFooter className='mt-8 flex-col gap-2 sm:flex-col sm:space-x-0'>
          {mode === 'details' ? (
            <div className='flex w-full flex-col gap-2 sm:flex-row sm:justify-end'>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {canManage ? (
                <>
                  <Button
                    variant='destructive'
                    onClick={() => setCancelConfirmOpen(true)}
                    disabled={cancelMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button onClick={() => setMode('reschedule')}>
                    Reschedule
                  </Button>
                </>
              ) : null}
            </div>
          ) : (
            <div className='flex w-full flex-col gap-2 sm:flex-row sm:justify-end'>
              <Button variant='outline' onClick={() => setMode('details')}>
                Back
              </Button>
              <Button
                onClick={() => void handleReschedule(false)}
                disabled={!canSubmitReschedule || rescheduleMutation.isPending}
              >
                {rescheduleMutation.isPending && !capacityOverrideOpen ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Saving…
                  </>
                ) : (
                  'Save reschedule'
                )}
              </Button>
            </div>
          )}
        </SheetFooter>
      </SheetContent>

      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this makeup?</AlertDialogTitle>
            <AlertDialogDescription>
              This releases the makeup credit back to the family and frees the
              absence for rebooking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelMutation.isPending}>
              Keep booking
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={cancelMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void handleCancel();
              }}
            >
              {cancelMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Cancelling…
                </>
              ) : (
                'Cancel makeup'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={capacityOverrideOpen}
        onOpenChange={(nextOpen) => {
          if (!rescheduleMutation.isPending) {
            setCapacityOverrideOpen(nextOpen);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Class is at capacity</AlertDialogTitle>
            <AlertDialogDescription>
              {capacityOverrideMessage ||
                'This class is full for the selected date. You can still reschedule as a staff override.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rescheduleMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={rescheduleMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void handleReschedule(true);
              }}
            >
              {rescheduleMutation.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving…
                </>
              ) : (
                'Reschedule anyway'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
